import type { ProductBacklogCard as Card } from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useTranslation } from 'react-i18next';
import { backlogCellOf } from '../../lib/product-backlog-view.js';

/**
 * 案件の札1枚（読むだけ）。1行目に区分の中の順位・題名・キー（小さく）、2行目に Backlog の状態と PL。
 * 題名が空ならキー、キーも無ければ行番号を見出しにする。focused はキーで開いた札（色を付ける）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の札1枚に、区分の中の順位・題名・Backlog キー・Backlog の状態・PL を出す（Backlog に見つからない・読めないも出し分ける）
 * @graph-connects backlogCellOf [calls] Backlog の欄
 */
export function ProductBacklogCard({
  card,
  backlogStatus,
  focused,
}: {
  card: Card;
  backlogStatus: 'ok' | 'unavailable';
  focused: boolean;
}) {
  const { t } = useTranslation();
  const heading =
    card.title ?? card.key ?? t('productBacklog.rowOnly', { row: String(card.rowNumber) });
  const cell = backlogCellOf(card, backlogStatus);

  return (
    <div
      data-key={card.key ?? undefined}
      aria-current={focused ? 'true' : undefined}
      className={`flex items-start gap-2.5 border-b border-[var(--color-border)] px-3.5 py-2.5 last:border-b-0 ${
        focused
          ? 'bg-[var(--color-accent-muted)] shadow-[inset_3px_0_0_var(--color-accent-primary)]'
          : ''
      }`}
    >
      <span className="w-[22px] flex-none pt-0.5 text-right text-xs font-semibold tabular-nums text-[var(--color-text-muted)]">
        {card.rank}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div className="flex min-w-0 items-baseline gap-2.5">
          <span
            className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--color-text-primary)]"
            title={heading}
          >
            {heading}
          </span>
          {card.key !== null && card.title !== null && (
            <span className="whitespace-nowrap text-[11px] font-medium text-[var(--color-text-muted)]">
              {card.key}
            </span>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-2 text-[12.5px]">
          {cell.kind === 'values' ? (
            <>
              <Badge>{cell.status ?? t('productBacklog.none')}</Badge>
              <span className="ml-auto flex-none text-xs text-[var(--color-text-muted)]">
                {t('productBacklog.pl', { name: cell.pl ?? t('productBacklog.none') })}
              </span>
            </>
          ) : (
            <span className="text-[var(--color-text-muted)]">
              {cell.kind === 'notFound'
                ? t('productBacklog.backlogNotFound')
                : cell.kind === 'noKey'
                  ? t('productBacklog.noKey')
                  : t('productBacklog.none')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
